'use client';

import { loadDIModules } from '@actr/di/src/hoc/load-di-modules';
import { useInject } from '@actr/di/src/hook/use-inject';
import { aviaSearchFormDirections } from '@actr/specs/src/avia/search-form-directions';
import aviaSearchDIModule from '@actr/stores/src/modules/avia/search';
import seoReviewsDIModule from '@actr/stores/src/modules/seo/reviews';
import seoTravelPointsDIModule from '@actr/stores/src/modules/seo/travel-points';
import { AviaSearchFormStore } from '@actr/stores/src/stores/stores/avia/search-form';
import { SeoTravelPointsStore } from '@actr/stores/src/stores/stores/seo/travel-points';
import { Sizes } from '@actr/style/src/specs/theme-v2';
import { Space } from '@actr/ui/src/components/_atoms/space';
import { Container } from '@actr/ui/src/components/container';
import { ContentBlock } from '@actr/ui/src/components/content-block';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { FC, PropsWithChildren, useEffect } from 'react';

import CountryPopularDirections from '@/components/avia/country/popular-directions';
import CountryProfitableDirections from '@/components/avia/country/profitable-directions';
import SearchFormHeader from '@/components/avia/search-form-header';
import Breadcrumb from '@/components/common/breadcrumb';
import ServiceAdvantages from '@/components/page-constucts/advantages';
import Faq from '@/components/page-constucts/faq';
import InnerHtmlTextBlock from '@/components/page-constucts/inner-html-text-block';
import ReviewsBlock from '@/components/page-constucts/reviews-block';
import SideNavBar from '@/components/page-constucts/side-navigation-bar';
import TravelPointsList, { PointType } from '@/components/page-constucts/travel-points-list';
import WhereToFly from '@/components/page-constucts/where-to-fly';
import { SeoCountry } from '@/services/models/avia/country';
import { getContentBlocks } from '@/utils/get-content-blocks';
import getNavBarItems from '@/utils/nav-bar';

import * as Styled from './style.css';

export type BaseCountryPageProps = {
  data: SeoCountry;
  path: string;
}

const BaseCountryPage: FC <BaseCountryPageProps & PropsWithChildren> = observer(({ data, children, path }) => {
  const { search_form_title, breadcrumbs, content, destination_country } = data;
  const { info, from_country_cities, where_to_fly,
    faq, feedback, advantages,
    app_banner, from_country_popular_directions, from_country_profitable_directions } = content || {};
  const aviaSearchForm = useInject(AviaSearchFormStore);
  const travelPointsStore = useInject(SeoTravelPointsStore);
  const itemsForNavBar = getNavBarItems(content);

  useEffect(() => {
    const defaultSegments = [{ ...aviaSearchForm.defaultSegment }];

    aviaSearchForm.stopStore();
    runInAction(() => {
      aviaSearchForm.setSegments(defaultSegments);
      aviaSearchForm.setDirection(aviaSearchFormDirections.TWOWAY);
    });
  }, [aviaSearchForm]);

  useEffect(() => {
    if (from_country_cities?.list) {
      travelPointsStore.setPoints(from_country_cities.list);
    }
  }, [from_country_cities, from_country_cities?.list, travelPointsStore]);

  const crumbFrom = [
    {
      title: 'Авиабилеты',
      path: '/',
    }, {
      path: '/countries',
      title: 'Страны',
    }, {
      path: `/countries/${path}`,
      title: breadcrumbs,
    }];

  const contentBlocks = content ? [
    {
      key: 'where_to_fly',
      condition: !!where_to_fly && !!where_to_fly.list,
      render: () => (
        where_to_fly ? (
          <WhereToFly
            title={where_to_fly.title}
            subtitle={where_to_fly.subtitle}
            cities={where_to_fly.list.cities}
            countries={where_to_fly.list.countries}
            directions={where_to_fly.list.directions}
            allCitiesLink={destination_country}
          />
        ) : null
      )
    },
    {
      key: 'faq',
      condition: !!faq?.list,
      render: () => (
        faq ? (
          <Faq list={faq?.list} title={faq.title} />
        ) : null
      )
    },
    {
      key: 'feedback',
      condition: !!feedback?.list,
      render: () => (
        feedback ? (
          <ReviewsBlock title={feedback.title} feedback={feedback?.list} />
        ) : null
      )
    },
    {
      key: 'advantages',
      condition: !!advantages?.list?.enable,
      render: () => (<ServiceAdvantages />)
    },
    {
      key: 'from_country_cities',
      condition: !!from_country_cities?.list,
      render: () => from_country_cities ? (
        <TravelPointsList
          id="from_country_cities"
          title={from_country_cities.title}
          description={from_country_cities.description}
          type={PointType.CITY}
          pointsList={from_country_cities?.list || {}}
        />
      ) : null
    },
    {
      key: 'info',
      condition: !!info?.description,
      render: () => info ? (
        <ContentBlock id="country_information">
          <InnerHtmlTextBlock
            html={info.description}
          />
        </ContentBlock>
      ) : null
    },
    {
      key: 'from_country_popular_directions',
      condition: !!from_country_popular_directions?.list?.length,
      render: () => from_country_popular_directions ? (
        <CountryPopularDirections
          title={from_country_popular_directions.title}
          list={from_country_popular_directions.list}
        />
      ) : null
    },
    {
      key: 'from_country_profitable_directions',
      condition: !!from_country_profitable_directions?.list?.length,
      render: () => from_country_profitable_directions ? (
        <CountryProfitableDirections
          title={from_country_profitable_directions.title}
          list={from_country_profitable_directions.list}
        />
      ) : null
    },
  ] : [];

  const blocks = getContentBlocks({
    content, contentBlocks
  });

  return (
    <>
      <SearchFormHeader
        title={search_form_title || ''}
      />
      <Container>
        <Breadcrumb crumb={crumbFrom} dynamic={false} />
        <Space size={Sizes.M} />
        <Container className={Styled.ColumnWrapper}>
          <SideNavBar items={itemsForNavBar} isAppBannerEnable={!!app_banner?.list?.enable} isHideNavBarOnMobile={true} />
          <div className={Styled.MainInfo}>
            {blocks.map(block => (
              <React.Fragment key={block.key}>
                {block.render()}
              </React.Fragment>
            ))}
          </div>
        </Container>
        {children}
      </Container>
    </>
  );
});

export default loadDIModules(BaseCountryPage, aviaSearchDIModule, seoReviewsDIModule, seoTravelPointsDIModule);
